import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { useUnreadCount } from '@/modules/chat/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { useEmailUnreadCount } from './header/useEmailUnreadCount';

/**
 * MobileNavBar — bottom-nav global do shell mobile (R0.4, mockup app.html).
 *
 * Slots do mockup: Atend.(badge) | CRM | FAB "+" | Inbox(badge) | Mais.
 * "Mais" abre um action-sheet com o restante da navegação principal.
 * O FAB dispara o CustomEvent global `troia:mobile-fab` — cada tela que tem
 * ação primária de criação escuta e abre seu fluxo (drawer/página). Se
 * nenhuma tela tratar (event.defaultPrevented === false), o FAB abre o sheet
 * "Mais" como fallback (nunca fica morto).
 *
 * Renderizada apenas em < lg (o desktop usa a Sidebar). O <main> ganha
 * padding-bottom correspondente no MainLayout.
 */

/**
 * Quem vê cada item (20/10/2026): a mesma regra da trava das telas
 * (`lib/route-access.ts`). Item de GRUPO lista as telas do grupo na ordem do
 * menu lateral e leva à primeira que a pessoa pode abrir; se não pode abrir
 * nenhuma, o item some. Antes só "Minhas Vendas" e "CSAT" eram conferidos — e
 * um comentário aqui dizia que a rota já era protegida, o que não era verdade.
 */
const MORE_ITEMS: Array<{ label: string; icon: IconName; paths: string[] }> = [
  { label: 'Minhas Vendas', icon: 'award', paths: ['/dashboard/management/sales'] },
  {
    label: 'Dashboards',
    icon: 'bar-chart',
    paths: [
      '/dashboard/management/support',
      '/dashboard/management/commercial',
      '/dashboard/management/marketing',
      '/dashboard/management/analytics',
    ],
  },
  { label: 'Agenda', icon: 'calendar', paths: ['/calendar'] },
  { label: 'Help Desk', icon: 'headphones', paths: ['/helpdesk/tickets', '/helpdesk/projects', '/checklists', '/checklists/my'] },
  {
    label: 'Automação',
    icon: 'zap',
    paths: [
      '/automation/ai-agents',
      '/automation/workflows',
      '/automation/skills',
      '/automation/custom-actions',
      '/automation/voices',
      '/automation/forms',
    ],
  },
  { label: 'Prospecção', icon: 'megaphone', paths: ['/prospecting/campaigns', '/prospecting/templates', '/prospecting/audiences'] },
  { label: 'Relatórios', icon: 'trending-up', paths: ['/reports/crm'] },
  { label: 'CSAT', icon: 'star', paths: ['/reports/csat'] },
  { label: 'Bases de dados', icon: 'database', paths: ['/databases'] },
  { label: 'Websites', icon: 'globe', paths: ['/websites'] },
  {
    label: 'Configurações',
    icon: 'settings',
    paths: [
      '/settings/units',
      '/settings/tags',
      '/settings/levels',
      '/settings/users',
      '/settings/teams',
      '/settings/shifts',
      '/settings/goals',
      '/settings/gamification',
      '/settings/email-domains',
      '/settings/channels',
      '/settings/sla-policies',
    ],
  },
];

export const MobileNavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { data: chatUnread = 0 } = useUnreadCount();
  const { data: emailUnread = 0 } = useEmailUnreadCount();
  const { canAccessPath } = usePermissions();
  const moreItems = MORE_ITEMS.flatMap((item) => {
    const path = item.paths.find((p) => canAccessPath(p));
    return path ? [{ label: item.label, icon: item.icon, path }] : [];
  });
  const showChat = canAccessPath('/chat');
  const showCrm = canAccessPath('/crm');
  const showEmail = canAccessPath('/email');

  const isActive = (prefix: string) => location.pathname.startsWith(prefix);

  const handleFab = () => {
    const event = new CustomEvent('troia:mobile-fab', { cancelable: true });
    window.dispatchEvent(event);
    // Nenhuma tela tratou a ação primária → fallback: abre o sheet "Mais"
    if (!event.defaultPrevented) setMoreOpen(true);
  };

  const NavItem: React.FC<{
    label: string;
    icon: IconName;
    path: string;
    badge?: number;
  }> = ({ label, icon, path, badge }) => {
    const active = isActive(path === '/chat' ? '/chat' : path);
    return (
      <button
        type="button"
        onClick={() => { setMoreOpen(false); navigate(path); }}
        className="relative flex flex-col items-center justify-start gap-0.5 flex-1"
        style={{ color: active ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }}
        data-testid={`mobile-nav-${label.toLowerCase().replace(/\s|\./g, '')}`}
      >
        <span className="relative">
          <Icon name={icon} className="w-[22px] h-[22px]" />
          {typeof badge === 'number' && badge > 0 && (
            <span
              className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center tabular-nums"
              style={{ backgroundColor: '#ef4444' }}
            >
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        <span className="text-[10.5px] font-semibold">{label}</span>
      </button>
    );
  };

  return (
    <>
      {/* Sheet "Mais" (tema claro fixo, action-sheet do DS §3.14) */}
      {moreOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-[60]"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.4)' }}
            onClick={() => setMoreOpen(false)}
          />
          <div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-[61] rounded-t-2xl p-2 pb-[max(8px,env(safe-area-inset-bottom))]"
            style={{ backgroundColor: '#ffffff', boxShadow: '0 -8px 32px rgba(0,0,0,.16)' }}
            data-testid="mobile-nav-more-sheet"
          >
            <div className="w-9 h-1 rounded-full mx-auto mb-2" style={{ backgroundColor: '#e2e8f0' }} />
            <div className="grid grid-cols-3 gap-1">
              {moreItems.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => { setMoreOpen(false); navigate(item.path); }}
                  className="flex flex-col items-center gap-1.5 rounded-xl py-3"
                  style={{ color: '#374151' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid={`mobile-more-${item.path.replace(/\W/g, '')}`}
                >
                  <Icon name={item.icon} className="w-5 h-5" style={{ color: '#64748b' }} />
                  <span className="text-ds-sm font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Barra inferior */}
      <nav
        className="min-[900px]:hidden fixed bottom-0 left-0 right-0 z-50 flex items-start px-1.5 pt-[9px]"
        style={{
          height: 'calc(72px + env(safe-area-inset-bottom))',
          paddingBottom: 'env(safe-area-inset-bottom)',
          // Conteúdo da página rola por trás da barra — tint sobre base opaca
          background: 'var(--theme-overlay-header-background)',
          borderTop: '1px solid var(--theme-header-border)',
        }}
        data-testid="mobile-nav-bar"
      >
        {/* Slot sem permissão vira espaço vazio: o FAB continua no centro. */}
        {showChat ? <NavItem label="Atend." icon="message-circle" path="/chat" badge={chatUnread} /> : <div className="flex-1" />}
        {showCrm ? <NavItem label="CRM" icon="trending-up" path="/crm" /> : <div className="flex-1" />}

        {/* FAB central — squircle 46px, glow roxo, deslocamento -8px (mockup .m-fab) */}
        <div className="flex-1 flex items-start justify-center">
          <button
            type="button"
            onClick={handleFab}
            className="w-[46px] h-[46px] flex items-center justify-center text-white"
            style={{
              marginTop: -8,
              borderRadius: 16,
              backgroundColor: 'var(--theme-button-primary)',
              boxShadow: '0 6px 16px rgba(139,92,246,.4)',
            }}
            aria-label="Criar"
            data-testid="mobile-nav-fab"
          >
            <Icon name="plus" className="w-[22px] h-[22px]" />
          </button>
        </div>

        {showEmail ? <NavItem label="Inbox" icon="inbox" path="/email" badge={emailUnread} /> : <div className="flex-1" />}

        {/* Mais — abre o sheet (não navega) */}
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="relative flex flex-col items-center justify-start gap-0.5 flex-1"
          style={{ color: moreOpen ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }}
          data-testid="mobile-nav-mais"
        >
          <Icon name="more-horizontal" className="w-[22px] h-[22px]" />
          <span className="text-[10.5px] font-semibold">Mais</span>
        </button>
      </nav>
    </>
  );
};

MobileNavBar.displayName = 'MobileNavBar';
